/**
 * Server state for the reports and exports. One read per report, keyed on the filters. A report is fetched
 * again whenever it is opened, since confirming a receipt or recording a payment anywhere in the
 * app changes it; while a new filter loads, the previous numbers stay on screen rather than
 * blanking.
 */

import { keepPreviousData, queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import { unwrap } from "@/api/problem";
import type { components, paths } from "@/api/schema";
import type { ExportBody } from "./exportForm";
import { toQuery, type Filters } from "./filters";

export type ExpenseReport = components["schemas"]["ExpenseReportOut"];
export type IncomeReport = components["schemas"]["IncomeReportOut"];
export type OrderReport = components["schemas"]["OrderReportOut"];
export type TaxReport = components["schemas"]["TaxReportOut"];

type Query<P extends keyof paths> = paths[P] extends { get: { parameters: { query?: infer Q } } }
  ? NonNullable<Q>
  : never;

const common = { placeholderData: keepPreviousData } as const;

export function expensesQuery(filters: Filters) {
  return queryOptions({
    queryKey: ["report", "expenses", toQuery(filters)],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/reports/expenses", {
          params: { query: toQuery(filters) as Query<"/api/v1/reports/expenses"> },
        }),
      ),
    ...common,
  });
}

export function householdQuery(filters: Filters) {
  return queryOptions({
    queryKey: ["report", "household", toQuery(filters)],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/reports/household", {
          params: { query: toQuery(filters) as Query<"/api/v1/reports/household"> },
        }),
      ),
    ...common,
  });
}

export function incomeQuery(filters: Filters) {
  return queryOptions({
    queryKey: ["report", "income", toQuery(filters)],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/reports/income", {
          params: { query: toQuery(filters) as Query<"/api/v1/reports/income"> },
        }),
      ),
    ...common,
  });
}

export function ordersReportQuery(filters: Filters) {
  return queryOptions({
    queryKey: ["report", "orders", toQuery(filters)],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/reports/orders", {
          params: { query: toQuery(filters) as Query<"/api/v1/reports/orders"> },
        }),
      ),
    ...common,
  });
}

// --- exports ------------------------------------------------------------------------------------

export type Export = components["schemas"]["ExportOut"];
export type ExportKind = components["schemas"]["ExportKind"];

function inProgress(exports: Export[] | undefined): boolean {
  return (exports ?? []).some((e) => e.status === "queued" || e.status === "running");
}

/** Recent exports of one kind: the accountant's CSVs, or the full exports of everything. Polls
 * every two seconds while one is being made, and not otherwise: the row is how the worker's
 * progress is seen (CLAUDE.md, "Async work"). */
export function exportsQuery(kind: ExportKind) {
  return queryOptions({
    queryKey: ["exports", kind],
    queryFn: async () => unwrap(await api.GET("/api/v1/exports", { params: { query: { kind } } })),
    refetchInterval: (query) => (inProgress(query.state.data) ? 2000 : false),
  });
}

export function useRequestExport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: ExportBody) => unwrap(await api.POST("/api/v1/exports", { body })),
    onSuccess: async (made) => {
      // Show it at once, then let the list's polling take over. A full export asked for while
      // one is being made comes back as that same one, so it is not listed twice.
      queryClient.setQueryData(exportsQuery(made.kind).queryKey, (old) => [
        made,
        ...(old ?? []).filter((item) => item.id !== made.id),
      ]);
      await queryClient.invalidateQueries({ queryKey: ["exports"] });
    },
  });
}

/**
 * Download an export through a link signed now: the one in the list may be minutes old and past
 * its expiry. The route also notes that the owner took the file, which is what deleting the
 * photos a downgrade left waits for, so the photo summary is asked for again. A failure means
 * the file is gone.
 */
export function useDownloadExport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (exportId: string) =>
      unwrap(
        await api.POST("/api/v1/exports/{export_id}/download", {
          params: { path: { export_id: exportId } },
        }),
      ),
    onSuccess: async ({ url }) => {
      window.location.assign(url);
      await queryClient.invalidateQueries({ queryKey: ["receipt-photos"] });
    },
  });
}

export function taxQuery(filters: Filters) {
  return queryOptions({
    queryKey: ["report", "tax", toQuery(filters)],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/reports/tax", {
          params: { query: toQuery(filters) as Query<"/api/v1/reports/tax"> },
        }),
      ),
    ...common,
  });
}
